import type { Territory } from '@amzn/vinyl-amzn'
import { getMarketplace } from '@/marketplace'
import { DeviceType, globalRef } from '@amazon/vinyl-util'
import { desktopAudibleAvailable } from '@/audible/desktopAudible'
import { getDeviceConfig } from '@/device/deviceConfig'
import { usesLwaAuth } from '@/auth/authRef'
import { type WeblabMarketplace, weblabMarketplace } from '@/territory'

// GH's audiobooks weblab is T1 here and control in IN, AV_ROW_EU, AV_ROE and AV_ROW_NA.
const AUDIOBOOK_MARKETPLACES: ReadonlySet<WeblabMarketplace> = new Set<WeblabMarketplace>([
    'US',
    'CA',
    'MX',
    'BR',
    'GB',
    'DE',
    'FR',
    'IT',
    'ES',
    'JP',
    'AU',
    'AV_ROW_FE',
])

/** Whether audiobooks are offered in the territory's marketplace, as GH's audiobooks weblab allocates it. */
export function isAudiobooksMarket(territory: Territory): boolean {
    return AUDIOBOOK_MARKETPLACES.has(weblabMarketplace(territory))
}

function installedDesktop(): boolean {
    const { installed, deviceType } = getDeviceConfig()
    return installed && deviceType === DeviceType.DESKTOP
}

// The shell's last answer, for the synchronous gate; false until the shell first answers.
const shellPlaysAudiobooksRef = globalRef(() => ({ value: false }))

/** The installed desktop app plays audiobooks only where its shell can: see {@link desktopAudibleAvailable}. */
async function audiobooksOnThisDevice(): Promise<boolean> {
    if (!installedDesktop()) return true
    const plays = await desktopAudibleAvailable()
    shellPlaysAudiobooksRef.value.value = plays
    return plays
}

/** Audible accepts cookie sessions and device tokens, and rejects an LWA token. */
function sessionAuthCanAuthenticateAudible(): boolean {
    return !usesLwaAuth()
}

/**
 * Whether this device and session can use audiobooks at all, whatever the territory. Synchronous,
 * so a request can drop its audiobook parts without waiting for the marketplace lookup; in the
 * installed desktop app it reads the shell's last answer.
 */
export function audiobooksSupportedForSession(): boolean {
    return (
        sessionAuthCanAuthenticateAudible() &&
        (!installedDesktop() || shellPlaysAudiobooksRef.value.value)
    )
}

/**
 * Whether the current session is offered audiobooks: its auth must be one Audible accepts, the
 * installed desktop app's shell must play them, and the account's territory (else the page host's)
 * must be an audiobooks marketplace.
 */
export async function audiobooksMarketEnabled(): Promise<boolean> {
    if (!sessionAuthCanAuthenticateAudible() || !(await audiobooksOnThisDevice())) return false
    return isAudiobooksMarket((await getMarketplace()).musicTerritory)
}

type RecommendedItem = { readonly node: { readonly __typename?: string } }
type RecommendationRow = {
    readonly node: {
        readonly recommendedContent?: { readonly edges: readonly RecommendedItem[] } | null
    }
}

/** `items` less their audiobooks, which GH leaves out of every other recommendation outside the audiobook marketplaces. */
export function withoutAudiobooks<T extends RecommendedItem>(items: readonly T[]): T[] {
    return items.filter((item) => item.node.__typename !== 'Audiobook')
}

/** `rows` with {@link withoutAudiobooks} applied to each row's items. */
export function withoutAudiobookItems<T extends RecommendationRow>(rows: readonly T[]): T[] {
    return rows.map((row) => {
        const content = row.node.recommendedContent
        if (!content) return row
        const node = {
            ...row.node,
            recommendedContent: { ...content, edges: withoutAudiobooks(content.edges) },
        }
        return { ...row, node } as T
    })
}
